<!--
@component
Bracket (entrypoint)

Prepares the bracket data once and composes the accessible-table fallback
with the visual bracket. Both views read from the same `matches` /
`thirdPlaceMatch` / `venues` so labels and scores stay in sync.

  Bracket/
    index.svelte        ← this file (data prep + composition)
    shared.ts           ← types + per-match label/score/winner helpers
    Accessible/         ← screen-reader/SEO table fallback
    Visual/             ← SVG + cards bracket
-->

<script lang="ts">
  import getBracketData from '$utils/getBracketData';
  import AccessibleBracket from './Accessible/index.svelte';
  import VisualBracket from './Visual/index.svelte';
  import type { Round } from './content';

  let {
    note = '',
    data,
    mobileBreakpoint = 900,
    heightContainer = $bindable(),
  }: {
    note?: string;
    data: any;
    mobileBreakpoint?: number;
    heightContainer?: number;
  } = $props();

  // Compute the bracket once and split into main rounds + third-place match.
  const allRounds = getBracketData(data.matches) as Round[];
  const matches = allRounds.filter((d) => d.round !== '3rd Place Final');
  const thirdPlaceMatch =
    allRounds.find((d) => d.round === '3rd Place Final') ?? null;

  // Hoist the venues array so both views share one reference. `getVenue`
  // keeps an O(1) id→venue index keyed on this array (see
  // src/utils/getMatchStats/getVenue.js, issue #29), so per-match lookups
  // don't pay the linear `.find()` cost.
  const venues = data?.matches?.venues?.venue ?? [];
</script>

<AccessibleBracket {matches} {thirdPlaceMatch} {venues} />

<VisualBracket
  {note}
  {matches}
  {thirdPlaceMatch}
  {venues}
  {mobileBreakpoint}
  bind:heightContainer
/>
